/**
 * @file   src\components\SignupStepMenu.tsx
 * @brief  Signup Step Menu component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { Link, Badge, useParams, useState, useEffect } from '../utils/thirdpartyComponents';
import Bankinfoicon from "../assets/images/bank-info-icon.svg";
import Appsettingsicon from "../assets/images/app-settings-icon.svg";
import Locationsicon from "../assets/images/locations-icon.svg";
import Orderhardwareicon from "../assets/images/order-hardware-icon.svg";
import VerifyIcon from '../assets/images/icons/verify';
import { pageURLs } from '../utils/pageUrls';
import {CompanyApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import { userType } from '../utils/enums';

// Define the props for SignupStepMenu
const SignupStepMenu = ({ activeStep, setActiveStep, completedSteps, cuserType }: { activeStep: string; setActiveStep: (step: string) => void; completedSteps: string[]; cuserType: string }) => {

  // Define the steps with their details
  const steps = (cuserType === userType['CARWASH-OWNER']) ? 
  [
    { id: 'bank', title: 'Merchant Payment Information', icon: Bankinfoicon, description: 'Add your Payment Method to process customer payments easily.' },
    { id: 'settings', title: 'Custom Branding', icon: Appsettingsicon, description: 'Upload your logo and select a theme to match your company’s branding to enhance brand recall.' },
    { id: 'locations', title: 'Locations', icon: Locationsicon, description: 'Add your service locations and reach out to all your prospective customers out there.' },
    { id: 'hardware', title: 'Order Hardware', icon: Orderhardwareicon, description: 'Speed up the process of delivering hardware to your locations in a single step.' }
  ] : 
  [
    { id: 'bank', title: 'Merchant Payment Information', icon: Bankinfoicon, description: 'Add your Payment Method to process customer payments easily.' },
    { id: 'settings', title: 'Custom Branding', icon: Appsettingsicon, description: 'Upload your logo and select a theme to match your company’s branding to enhance brand recall.' },
    { id: 'locations', title: 'Locations', icon: Locationsicon, description: 'Add your service locations and reach out to all your prospective customers out there.' }
  ] ;

  const param = useParams();
  // State to company details
  const [company, setCompany] = useState({

      companyName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: ""
  });
  // To get company details
  const getCompanyAddress = async () => {
      try {
          const param3 = `${param.id}`;
            const { data } = (cuserType !== userType['CARWASH-OWNER']) ? 
            await axios.get(CompanyApi.GET_COMPANY_ADDRESS + param3)
            : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
            if (data?.error === 0) {
                setCompany(data.data);
            }

      } catch (error: any) {
          throw new Error(error.response?.data?.message || error.message);
      }
  }
  // Fetch company information on component mount
    useEffect(() => {
        getCompanyAddress();
    }, []);



  return (
    <div className='signup-step-container'>
      <div className="title-group">
        <p className='f-bold'>{cuserType !== userType['CARWASH-OWNER'] ? "" : "Welcome,"}</p>
        <h1 className='f-semi'>{company.companyName}</h1>
        <p className='f-bold'>Please complete your company setup in the few steps below.
          This info can be provided now or later, but is mandatory for the system to go live.</p>
      </div>
      <div className="step-menu-wrap">
        {steps.map(step => (
          <div
            key={step.id}
            // Apply 'active' class if this step is the active step, 'completed' class if it is in the completed steps
            className={`step-menu d-flex ${activeStep === step.id ? 'active' : ''} ${completedSteps.includes(step.id) ? 'completed' : ''}`}
            // Update active step on click
            onClick={() => setActiveStep(step.id)}
          >
            {!completedSteps.includes(step.id) ? <Badge className="to-do">To Do</Badge> : null}
            <div className="icon"
            ><img src={step.icon} alt="Car wash app" /></div>
            <div className="info">
              <div className="title d-flex align-items-center"
              >
                <h6 className='f-bold'>{step.title}</h6>
                <VerifyIcon />
              </div>
              <p className='f-medium'
              >{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {cuserType !== userType['CARWASH-OWNER'] ? <Link to={`/superadmin/${pageURLs.dashboard}`} className='f-bold skip-link'>I will do this later. Take me to my Dashboard.</Link> : <Link to={`/${pageURLs.dashboard}`} className='f-bold skip-link'>I will do this later. Take me to my Dashboard.</Link>}
    </div>
  );
};
export default SignupStepMenu;
